"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { usePathname } from "next/navigation";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { unwrapApiData } from "@/lib/apiResponse";

type CompanyNotice = { noticeId: number; title: string; summary?: string | null };

const API = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5016";
const todayInKorea = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
const guestDismissalKey = (id: number) =>
  "seexr:notice-banner:guest:" + id + ":" + todayInKorea();

function isCompanyNotice(item: unknown): item is CompanyNotice {
  if (!item || typeof item !== "object") return false;
  const value = item as Partial<CompanyNotice>;
  return Number.isSafeInteger(value.noticeId) && (value.noticeId as number) > 0 &&
    typeof value.title === "string" && value.title.length > 0 &&
    (value.summary === undefined || value.summary === null ||
      (typeof value.summary === "string" && Array.from(value.summary).length <= 100));
}

function isHiddenForGuest(id: number): boolean {
  try { return localStorage.getItem(guestDismissalKey(id)) === "1"; }
  catch { return false; }
}

function companyNewsUrl(id: number): string {
  const domain = window.location.hostname.endsWith(".dev") ? "seexr.dev" : "seexr.co.kr";
  return "https://www." + domain + "/notice/" + id;
}

/** Non-blocking toast: portal keeps notice outside workspace layout and clipping. */
export function CompanyNoticeToast() {
  const [mounted, setMounted] = useState(false);
  const [notices, setNotices] = useState<CompanyNotice[]>([]);
  const [hiddenThisTab, setHiddenThisTab] = useState<number[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLanding, setIsLanding] = useState(false);
  const [composerBounds, setComposerBounds] = useState<{ left: number; width: number } | null>(null);
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  // The pathname alone cannot tell whether a chat/run has started.
  // Each workspace supplies a marker only while its landing UI is active.
  useEffect(() => {
    const isLandingRoute = pathname === "/" || /^\/ah\/[^/]+\/?$/.test(pathname);
    if (!isLandingRoute) return;
    const root = document.getElementById("main-content");
    if (!root) return;
    // Measure the *actual* composer outer border, not a guessed max-width.
    // The risk assessment composer can be 804px while chat is 720px.
    let activeComposer: Element | null = null;
    const sizes = new ResizeObserver(() => update());
    const update = () => {
      const hasSession = new URLSearchParams(window.location.search).has("session");
      const landing = root.querySelector('[data-seexr-notice-landing="true"]');
      setIsLanding(!hasSession && Boolean(landing));
      const composer = landing?.querySelector('[data-seexr-notice-composer]') ?? null;
      if (composer !== activeComposer) {
        if (activeComposer) sizes.unobserve(activeComposer);
        if (composer) sizes.observe(composer);
        activeComposer = composer;
      }
      if (!composer) {
        setComposerBounds((previous) => previous === null ? previous : null);
        return;
      }
      const rect = composer.getBoundingClientRect();
      const next = { left: rect.left, width: rect.width };
      setComposerBounds((previous) =>
        previous &&
        Math.abs(previous.left - next.left) < 0.5 &&
        Math.abs(previous.width - next.width) < 0.5 ? previous : next,
      );
    };
    sizes.observe(root);
    const frame = window.requestAnimationFrame(update);
    const observer = new MutationObserver(update);
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-seexr-notice-landing"],
    });
    window.addEventListener("popstate", update);
    window.addEventListener("resize", update);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      sizes.disconnect();
      window.removeEventListener("popstate", update);
      window.removeEventListener("resize", update);
    };
  }, [pathname]);

  // On narrow screens, never overlap the active composer or software keyboard.
  useEffect(() => {
    const update = () => {
      setIsComposerFocused(Boolean(document.activeElement?.matches(
        'input:not([type="hidden"]), textarea, [contenteditable="true"]',
      )));
    };
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    return () => {
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
    };
  }, []);

  useEffect(() => {
    const update = () => setRefreshKey((key) => key + 1);
    const whenVisible = () => { if (document.visibilityState === "visible") update(); };
    window.addEventListener("seexr:resume", update);
    window.addEventListener("focus", update);
    document.addEventListener("visibilitychange", whenVisible);
    const interval = window.setInterval(whenVisible, 5 * 60 * 1000);
    return () => {
      window.removeEventListener("seexr:resume", update);
      window.removeEventListener("focus", update);
      document.removeEventListener("visibilitychange", whenVisible);
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        let response = await fetchWithAuth(
          API + "/api/community/notices/banners/me?surface=app",
          { skipRedirectOn401: true },
        );
        const authenticated = response.ok;
        if (response.status === 401) {
          response = await fetch(API + "/api/community/notices/banners?surface=app", {
            credentials: "include", cache: "no-store",
          });
        }
        if (!response.ok) return;
        const payload = unwrapApiData<unknown>(await response.json());
        if (cancelled) return;
        const results = Array.isArray(payload) ? payload.filter(isCompanyNotice) : [];
        setNotices(authenticated
          ? results
          : results.filter((notice) => !isHiddenForGuest(notice.noticeId)));
      } catch {
        // Company news is supplementary: failures must not interrupt work.
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const available = useMemo(
    () => notices.filter((notice) => !hiddenThisTab.includes(notice.noticeId)),
    [notices, hiddenThisTab],
  );
  const current = available[0] ?? null;

  // Closing this notice is only a tab-local action; no account/day suppression UI.
  const dismiss = () => {
    if (!current) return;
    setHiddenThisTab((hidden) => [...hidden, current.noticeId]);
  };

  const isLandingRoute = pathname === "/" || /^\/ah\/[^/]+\/?$/.test(pathname);
  if (!mounted || !isLandingRoute || !isLanding || !current) return null;

  return createPortal(
    <div
      className={["pointer-events-none fixed bottom-[calc(12px+env(safe-area-inset-bottom))] z-[90] flex justify-center md:bottom-6",
        composerBounds ? "" : "inset-x-3 md:left-[var(--sidebar-width-desktop,72px)] md:right-6",
      ].filter(Boolean).join(" ")}
      style={composerBounds ? { left: composerBounds.left, width: composerBounds.width } : undefined}
    >
      <aside
        role="region"
        aria-label="새 회사소식 알림"
        aria-live="polite"
        className={[
          "pointer-events-auto w-full min-w-0 overflow-hidden rounded-[20px] border border-slate-200/70 bg-white/95 px-3 py-3 sm:rounded-[22px] sm:px-4 sm:py-3.5 text-slate-900 shadow-[0_10px_44px_-17px_rgba(15,23,42,0.22),0_2px_12px_-5px_rgba(15,23,42,0.10)] backdrop-blur-xl dark:border-white/10 dark:bg-[#232327]/95 dark:text-slate-50 sm:px-[18px] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-300",
          isComposerFocused && "max-md:hidden",
        ].filter(Boolean).join(" ")}
      >
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex min-w-0 items-center gap-2">
              <p className="min-w-0 truncate text-[13px] font-semibold leading-5 text-slate-900 dark:text-slate-100">
                {current.title}
              </p>
              {available.length > 1 && (
                <span className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
                  외 {available.length - 1}건
                </span>
              )}
            </div>
            {current.summary?.trim() && (
              <p className="hidden break-words text-xs leading-5 text-slate-500 dark:text-slate-400 sm:block">
                {current.summary.trim()}
              </p>
            )}
          </div>
          <a
            href={companyNewsUrl(current.noticeId)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-seexr-solid px-2.5 py-2 text-[11px] sm:px-3.5 font-semibold leading-4 text-white transition-colors hover:bg-seexr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-seexr-primary sm:text-xs"
          >
            자세히 보기
          </a>
          <button
            type="button"
            onClick={dismiss}
            aria-label="이 알림 닫기"
            title="닫기"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-seexr-primary dark:hover:bg-white/10 dark:hover:text-slate-100"
          >
            <X size={15} aria-hidden="true" />
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  );
}
